// Rich-text editor for the email body template. Wraps the SAME TinyMCE editor the
// recipe/guides library uses (resourceLibrary/RichTextEditor) — which handles Hebrew
// RTL correctly — and adds an "insert {{field}}" menu that drops upstream-output tokens
// (e.g. {{createTrainee.username}}) into the body at the caret.
//
// The hand-rolled contentEditable version reversed Hebrew input; TinyMCE's directionality:"rtl"
// fixes that. Stores its value as an HTML string, same contract as before.

import {useRef, useState} from "react";
import {Box, IconButton, Menu, MenuItem, Tooltip, Typography} from "@mui/material";
import {DataObjectOutlined as FieldIcon} from "@mui/icons-material";
import {Editor} from "@tinymce/tinymce-react";
import {borders, colors} from "../../ui/colors";

const BRAND = "#01BBA7";
const TINYMCE_SRC = `${process.env.PUBLIC_URL || ""}/tinymce/tinymce.min.js`;

// Compact plugin/toolbar set for an email body (no image/table upload — those need the
// media pipeline the recipe editor wires; an email template stays text + basic formatting).
const PLUGINS = ["autolink", "lists", "link", "directionality", "autoresize"];
const TOOLBAR = "blocks | bold italic underline | forecolor | alignright aligncenter alignleft | rtl ltr | bullist numlist | link removeformat";

const CONTENT_STYLE = `
    body { font-family: -apple-system, "Helvetica Neue", Roboto, sans-serif; font-size: 14px;
           line-height: 1.6; direction: rtl; text-align: right; color: #1F2933; }
    a { color: #2563EB; }
    [style*="text-align: left"] { direction: ltr; }
    .field-token { background: ${BRAND}1A; color: ${BRAND}; border-radius: 4px;
                   padding: 0 4px; font-family: monospace; font-size: 12px; }
`;

export default function RichTextField({value, onChange, upstreamGroups = []}) {
    const editorRef = useRef(null);
    const [fieldMenu, setFieldMenu] = useState(null);

    const hasUpstream = upstreamGroups.some(g => g.fields.length);

    // Insert a {{path}} token as a styled, non-editable chip at the caret.
    const insertField = (path) => {
        setFieldMenu(null);
        const ed = editorRef.current;
        if (!ed) return;
        ed.insertContent(
            `<span class="field-token" contenteditable="false">{{${path}}}</span>&nbsp;`
        );
        ed.focus();
    };

    return (
        <Box sx={{border: borders.default, borderRadius: borders.radius.md, overflow: "hidden", bgcolor: colors.white,
                  "& .tox-tinymce": {border: "none"}}}>
            {/* Insert-field bar sits above the editor's own toolbar */}
            <Box sx={{display: "flex", alignItems: "center", gap: 0.5, px: 0.75, py: 0.25,
                      bgcolor: colors.bg, borderBottom: borders.default}}>
                <Tooltip title="הוסף שדה מהזרימה">
                    <span>
                        <IconButton size="small" disabled={!hasUpstream}
                                    onClick={e => setFieldMenu(e.currentTarget)} sx={{color: BRAND}}>
                            <FieldIcon sx={{fontSize: 17}}/>
                        </IconButton>
                    </span>
                </Tooltip>
                <Typography sx={{fontSize: "10px", color: colors.textMuted, ml: "auto", pr: 0.5}}>
                    {"הוסף {{שדה}} כדי לשלב נתונים"}
                </Typography>
            </Box>

            <Editor
                tinymceScriptSrc={TINYMCE_SRC}
                licenseKey="gpl"
                value={value || ""}
                onInit={(_e, editor) => { editorRef.current = editor; }}
                onEditorChange={(html) => onChange(html)}
                init={{
                    height: 200,
                    menubar: false,
                    branding: false,
                    promotion: false,
                    statusbar: false,
                    directionality: "rtl",
                    placeholder: "כתוב כאן את תוכן האימייל…",
                    plugins: PLUGINS,
                    toolbar: TOOLBAR,
                    toolbar_mode: "wrap",
                    block_formats: "פסקה=p; כותרת 1=h1; כותרת 2=h2; כותרת 3=h3",
                    content_style: CONTENT_STYLE,
                }}
            />

            <Menu anchorEl={fieldMenu} open={Boolean(fieldMenu)} onClose={() => setFieldMenu(null)}>
                {upstreamGroups.flatMap(g => [
                    <MenuItem key={`h-${g.sourceId}`} disabled sx={{fontSize: "11px", fontWeight: 700, color: `${BRAND} !important`, opacity: 1}}>
                        {g.sourceLabel}
                    </MenuItem>,
                    ...g.fields.map(f => (
                        <MenuItem key={f.path} onClick={() => insertField(f.path)} sx={{pr: 3}}>
                            <Box>
                                <Typography sx={{fontSize: "12px", fontFamily: "monospace"}}>{`{{${f.path}}}`}</Typography>
                                <Typography sx={{fontSize: "10px", color: colors.textMuted}}>{f.label}</Typography>
                            </Box>
                        </MenuItem>
                    )),
                ])}
            </Menu>
        </Box>
    );
}
